/* Modular product landing. No external libraries, personal data or order events. */
(function () {
  'use strict';
  if (window.LPCommerce) {
    window.LPCommerce.init(document);
    return;
  }

  const sections = new Map();
  const codOpenJobs = new Map();
  const codButtonStyles = new WeakMap();
  let groups = new Map();
  let stickyFrame = 0;
  let forwardingCodClick = false;
  const cartWriteGate = createCartWriteGate();
  // This opening button was observed and tested in the published Releasit DOM.
  // Scope it to this product's integration; never click an order-submit button.
  const codButtonSelector = '[data-lp-cod-integration] [id="_rsi-buy-now-button-overwrite"]';
  const sectionSelector = '[data-lp-section]';
  const keyFor = (section) => JSON.stringify([section.dataset.lpGroup || 'main', section.dataset.productId || '']);
  const sectionFor = (element) => element instanceof Element ? element.closest(sectionSelector) : null;
  const groupFor = (section) => section ? groups.get(keyFor(section)) : null;
  const elements = (group, selector) => group.sections.flatMap((section) => Array.from(section.querySelectorAll(selector)));
  const variantFor = (group) => group.data && group.data.variants.find((variant) => variant.id === group.variantId);
  const selectedPack = (group) => group && group.variantId ? [group.variantId, group.quantity, group.units].join(':') : '';
  const offerType = (group) => group && group.variantId ? group.codQuantityOffers ? 'cod_quantity' : group.quantity * group.units > 1 ? 'pack' : 'single' : '';

  function createCartWriteGate() {
    // Releasit restores the previous cart asynchronously when its dialog closes.
    // Observe completion without reading bodies, changing requests or touching
    // the cart. A new opening must wait for the whole clear -> add chain.
    const quietMs = 250;
    const pending = new Set();
    const requests = new WeakMap();
    const prototype = window.XMLHttpRequest && window.XMLHttpRequest.prototype;
    let owners = 0;
    let installed = false;
    let quietUntil = 0;
    let originalOpen;
    let originalSend;
    function watchedOpen(method, url) {
      const result = originalOpen.apply(this, arguments);
      let tracked = false;
      try {
        const target = new URL(url, window.location.href);
        tracked = owners > 0 && String(method).toUpperCase() !== 'GET'
          && String(method).toUpperCase() !== 'HEAD'
          && target.origin === window.location.origin
          && /\/cart\/(?:clear|add|update|change)(?:\.js)?\/?$/.test(target.pathname);
      } catch (_) { /* A URL the observer cannot parse retains native behavior. */ }
      requests.set(this, { tracked, active: null });
      return result;
    }
    function watchedSend() {
      const request = requests.get(this);
      if (!owners || !request || !request.tracked || request.active) return originalSend.apply(this, arguments);
      const xhr = this;
      const token = {};
      request.active = token;
      pending.add(token);
      let finished = false;
      const finish = () => {
        if (finished) return;
        finished = true;
        xhr.removeEventListener('loadend', finish);
        pending.delete(token);
        if (request.active === token) request.active = null;
        // The app retries a failed clear once after 2 s. Keep that gap closed
        // too, without retrying or handling the request ourselves.
        const settlingMs = xhr.status >= 200 && xhr.status < 300 ? quietMs : 2000 + quietMs;
        quietUntil = Math.max(quietUntil, Date.now() + settlingMs);
      };
      xhr.addEventListener('loadend', finish);
      try { return originalSend.apply(xhr, arguments); }
      catch (error) { finish(); throw error; }
    }
    return {
      acquire() {
        owners += 1;
        if (prototype && !installed) {
          originalOpen = prototype.open;
          originalSend = prototype.send;
          prototype.open = watchedOpen;
          prototype.send = watchedSend;
          installed = true;
        }
        let released = false;
        return () => {
          if (released) return;
          released = true;
          owners = Math.max(0, owners - 1);
          if (!owners && installed && prototype.open === watchedOpen && prototype.send === watchedSend) {
            prototype.open = originalOpen;
            prototype.send = originalSend;
            installed = false;
          }
        };
      },
      ready() {
        return pending.size === 0 && Date.now() >= quietUntil;
      }
    };
  }

  function publicDetail(section, group, extra) {
    const variant = group && variantFor(group);
    return Object.assign({
      section_id: section ? section.id : '',
      group: section ? section.dataset.lpGroup || 'main' : '',
      product_id: section ? section.dataset.productId || '' : '',
      product_handle: section ? section.dataset.productHandle || '' : '',
      variant_id: variant ? variant.id : '',
      quantity: group ? group.quantity : 0,
      units_per_item: group ? group.units : 1,
      selected_pack: selectedPack(group),
      offer_type: offerType(group),
      price_source: group && group.codQuantityOffers ? 'configured_cod_offer' : 'shopify_variant',
      currency: group && group.data ? group.data.currency : '',
      value: variant && group.quantity > 0 ? (selectionTotal(group) || 0) / 100 : 0
    }, extra || {});
  }

  function emit(name, section, group, extra) {
    (section && section.isConnected ? section : document).dispatchEvent(new CustomEvent(name, {
      bubbles: true,
      detail: publicDetail(section, group, extra)
    }));
  }

  function money(group, cents) {
    try {
      return new Intl.NumberFormat(group.data.locale || document.documentElement.lang || 'es', {
        style: 'currency', currency: group.data.currency
      }).format(cents / 100);
    } catch (_) {
      return (cents / 100).toFixed(2) + ' ' + group.data.currency;
    }
  }

  function perUnitPrice(group, cents, units) {
    const unitCents = cents / units;
    let digits = 2;
    try {
      digits = new Intl.NumberFormat(group.data.locale || 'es', { style: 'currency', currency: group.data.currency }).resolvedOptions().maximumFractionDigits;
    } catch (_) { /* Match the two decimal fallback in money(). */ }
    const scaled = unitCents / 100 * Math.pow(10, digits);
    const approximate = Math.abs(scaled - Math.round(scaled)) > 0.000001;
    return (approximate ? 'aprox. ' : '') + money(group, unitCents);
  }

  function availability(group, quantity) {
    if (group.packsReady === false) return { valid: false, message: 'Esta oferta no está disponible por el momento.' };
    if (group.nativeBlocked && !group.codQuantityOffers) return { valid: false, message: 'Esta oferta requiere el formulario de contraentrega.' };
    if (group.codQuantityOffers && offerTotalCents(group, group.variantId, quantity, group.units) === null) return { valid: false, message: 'Selecciona una cantidad de la oferta disponible.' };
    const variant = variantFor(group);
    if (!variant || !variant.available) return { valid: false, message: 'Agotado' };
    if (group.data.requiresSellingPlan) return { valid: false, message: 'Selecciona un plan de compra en la ficha habitual.' };
    const min = Math.max(1, Number(variant.minimum) || 1);
    const step = Math.max(1, Number(variant.increment) || 1);
    if (!Number.isInteger(quantity) || quantity < min || (quantity - min) % step !== 0) {
      return { valid: false, message: 'Elige una cantidad válida: mínimo ' + min + ', incrementos de ' + step + '.' };
    }
    if (variant.maximum > 0 && quantity > variant.maximum) return { valid: false, message: 'La cantidad supera el máximo permitido.' };
    if (variant.inventoryManaged && variant.inventoryPolicy === 'deny' && quantity > variant.inventoryQuantity) {
      return { valid: false, message: 'No hay disponibilidad para esta cantidad.' };
    }
    return { valid: true, message: '' };
  }

  function offerDefinitions(group) {
    const order = group.sections.find((section) => section.dataset.lpAnchor === 'order');
    const source = order || group.sections.find((section) => section.querySelector('[data-lp-pack]'));
    if (!source || !group.data) return [];
    return Array.from(source.querySelectorAll('[data-lp-pack]')).map((card) => ({
      variantId: card.dataset.variantId,
      quantity: Number(card.dataset.quantity),
      units: Number(card.dataset.unitsPerItem || 1),
      codTotalCents: Number(card.dataset.lpCodTotalCents || 0)
    })).filter((choice) => group.data.variants.some((variant) => variant.id === choice.variantId)
      && Number.isInteger(choice.quantity) && choice.quantity > 0
      && Number.isInteger(choice.units) && choice.units > 0);
  }

  function offerTotalCents(group, variantId, quantity, units) {
    if (!group.codQuantityOffers) return null;
    const matches = offerDefinitions(group).filter((choice) => choice.variantId === variantId && choice.quantity === quantity && choice.units === units);
    if (matches.length !== 1 || units !== 1) return null;
    const total = matches[0].codTotalCents;
    return Number.isSafeInteger(total) && total > 0 ? total : null;
  }

  function selectionTotal(group) {
    if (group.codQuantityOffers) return offerTotalCents(group, group.variantId, group.quantity, group.units);
    const variant = variantFor(group);
    return variant ? variant.price * group.quantity : null;
  }

  function packChoices(group) {
    return offerDefinitions(group).map((choice) => Object.assign(choice, {
      available: availability(Object.assign({}, group, { variantId: choice.variantId, units: choice.units, packsReady: true }), choice.quantity).valid
    }));
  }

  function choiceForVariant(group, variantId, quantity) {
    const choices = packChoices(group).filter((choice) => choice.variantId === variantId);
    if (group.codQuantityOffers && quantity !== undefined) return choices.find((choice) => choice.quantity === quantity);
    return choices.find((choice) => choice.available && choice.quantity === quantity)
      || choices.find((choice) => choice.available)
      || choices.find((choice) => choice.quantity === quantity)
      || choices[0];
  }

  function selectedOffer(group) {
    const units = group.quantity * group.units;
    if (!Number.isInteger(units) || units < 1) return 'Revisa la selección';
    if (group.units > 1 && group.quantity === 1) return 'Pack de ' + units + ' unidades';
    if (group.units > 1) return group.quantity + ' packs · ' + units + ' unidades';
    return units + (units === 1 ? ' unidad' : ' unidades');
  }

  function sync(group) {
    if (!group.data) return;
    const variant = variantFor(group);
    if (!variant) return;
    const status = availability(group, group.quantity);
    const displayQuantity = Number.isInteger(group.quantity) && group.quantity > 0 ? group.quantity : Math.max(1, variant.minimum || 1);
    const total = selectionTotal(group) ?? variant.price * displayQuantity;
    const compared = group.codQuantityOffers ? 0 : variant.compareAt * displayQuantity;
    group.sections.concat(elements(group, '[data-lp-native-form]')).forEach((scope) => {
      scope.dataset.selectedPack = selectedPack(group);
      scope.dataset.offerType = offerType(group);
    });
    elements(group, '[data-lp-selected-label], [data-lp-selected-offer]').forEach((node) => { node.textContent = selectedOffer(group); });
    elements(group, '[data-lp-selected-offer-summary]').forEach((node) => {
      const units = group.quantity * group.units;
      node.textContent = Number.isInteger(units) && units > 0
        ? units + (units === 1 ? ' frasco' : ' frascos')
        : 'Revisa la selección';
    });
    elements(group, '[data-lp-native-fallback]').forEach((fallback) => {
      fallback.hidden = true;
      fallback.querySelectorAll('input, select').forEach((input) => { input.disabled = true; });
    });
    elements(group, '[data-lp-price]').forEach((node) => { node.textContent = money(group, total); });
    elements(group, '[data-lp-compare-price]').forEach((node) => {
      node.hidden = !(compared > total);
      node.textContent = compared > total ? money(group, compared) : '';
    });
    elements(group, '[data-lp-stock]').forEach((node) => { node.textContent = status.message; });
    elements(group, '[data-lp-variant-input]').forEach((input) => {
      input.value = variant.id;
      input.setAttribute('value', variant.id);
      input.disabled = false;
    });
    elements(group, '[data-lp-quantity-input]').forEach((input) => {
      input.value = String(group.quantity);
      input.setAttribute('value', String(group.quantity));
      input.disabled = false;
    });
    elements(group, '[data-lp-variant-select]').forEach((select) => {
      select.value = variant.id;
      select.disabled = group.busy;
    });
    elements(group, '[data-lp-quantity-control]').forEach((input) => {
      input.value = String(group.quantity);
      input.min = String(variant.minimum || 1);
      input.step = String(variant.increment || 1);
      const max = variant.inventoryManaged && variant.inventoryPolicy === 'deny'
        ? Math.min(variant.maximum || Infinity, variant.inventoryQuantity)
        : variant.maximum || 0;
      if (max > 0 && Number.isFinite(max)) input.max = String(max);
      else input.removeAttribute('max');
      input.disabled = group.busy;
      input.setCustomValidity(status.valid ? '' : status.message);
    });
    elements(group, '[data-lp-pack]').forEach((card) => {
      const cardVariant = group.data.variants.find((candidate) => candidate.id === card.dataset.variantId);
      const cardQuantity = Number(card.dataset.quantity);
      const cardUnits = Number(card.dataset.unitsPerItem || 1);
      const cardGroup = Object.assign({}, group, { variantId: card.dataset.variantId, quantity: cardQuantity, units: cardUnits });
      card.disabled = group.busy || !cardVariant || !availability(cardGroup, cardQuantity).valid;
      card.setAttribute('aria-pressed', String(card.dataset.variantId === variant.id && cardQuantity === group.quantity && Number(card.dataset.unitsPerItem || 1) === group.units));
      const unitPrice = card.querySelector('[data-lp-unit-price]');
      const cardTotal = card.querySelector('[data-lp-card-total]');
      const selectedCardTotal = selectionTotal(cardGroup);
      if (cardTotal && selectedCardTotal !== null) cardTotal.textContent = money(group, selectedCardTotal);
      if (unitPrice && cardVariant && selectedCardTotal !== null) unitPrice.textContent = perUnitPrice(group, selectedCardTotal, cardQuantity * cardUnits) + ' por unidad';
      const savings = card.querySelector('[data-lp-quantity-savings]');
      const reference = card.querySelector('[data-lp-separate-reference]');
      const hasCodReference = group.codQuantityOffers && cardVariant && cardUnits === 1 && selectedCardTotal !== null;
      const separateTotal = cardVariant ? cardVariant.price * cardQuantity : 0;
      if (savings) {
        savings.hidden = !(hasCodReference && separateTotal > selectedCardTotal);
        savings.textContent = hasCodReference && separateTotal > selectedCardTotal
          ? 'Ahorras ' + money(group, separateTotal - selectedCardTotal) : '';
      }
      if (reference) {
        reference.hidden = !hasCodReference;
        const referencePrice = reference.querySelector('[data-lp-separate-price]');
        if (referencePrice) referencePrice.textContent = hasCodReference ? money(group, separateTotal) : '';
        else reference.textContent = hasCodReference ? 'Por separado: ' + money(group, separateTotal) : '';
      }
    });
    elements(group, '[data-lp-savings-note]').forEach((note) => {
      note.hidden = !group.codQuantityOffers;
      note.textContent = group.data.variants.length === 1
        ? 'Ahorro calculado sobre ' + money(group, variant.price) + ' por frasco.'
        : 'Ahorro calculado sobre el precio individual actual de cada variante.';
    });
    elements(group, '[data-lp-unit-summary]').forEach((node) => {
      const units = group.quantity * group.units;
      node.textContent = Number.isInteger(units) && units > 0
        ? units + (units === 1 ? ' unidad' : ' unidades') + ' · ' + perUnitPrice(group, total, units) + ' por unidad'
        : 'Revisa la cantidad seleccionada.';
    });
    elements(group, '[data-lp-submit]').forEach((button) => {
      button.disabled = group.nativeBlocked || group.busy || !status.valid;
      button.setAttribute('aria-disabled', String(button.disabled));
      const label = button.querySelector('[data-lp-submit-label]');
      if (label) label.textContent = group.busy ? 'Agregando…' : status.valid ? button.dataset.label : variant.available ? 'Revisar selección' : 'Agotado';
      const spinner = button.querySelector('[data-lp-loading]');
      if (spinner) spinner.hidden = !group.busy;
    });
    elements(group, '[data-lp-native-form]').forEach((form) => form.setAttribute('aria-busy', String(group.busy)));
    if (group.nativeBlocked) elements(group, '.lp-native-purchase').forEach((wrapper) => { wrapper.hidden = true; });
    if (group.codQuantityOffers) elements(group, '[data-lp-cod-integration]').forEach((wrapper) => { wrapper.hidden = !status.valid; });
    scheduleSticky();
  }

  function resolveAnchors(group) {
    elements(group, '[data-lp-target]').forEach((link) => {
      const target = group.sections.find((section) => section.dataset.lpAnchor === link.dataset.lpTarget);
      link.setAttribute('href', target ? '#' + target.id : '#MainContent');
    });
    elements(group, '[data-lp-pack-target]').forEach((card) => {
      const target = group.sections.find((section) => section.dataset.lpAnchor === card.dataset.lpPackTarget);
      if (target) card.setAttribute('aria-controls', target.id);
      else card.removeAttribute('aria-controls');
    });
  }

  function moveToSection(group, anchor) {
    const target = group.sections.find((section) => section.dataset.lpAnchor === anchor);
    if (!target) return false;
    target.setAttribute('tabindex', '-1');
    target.focus({ preventScroll: true });
    target.scrollIntoView({ behavior: window.matchMedia('(prefers-reduced-motion: reduce)').matches ? 'auto' : 'smooth', block: 'start' });
    return true;
  }

  function styleCodButton(order, button = order.querySelector(codButtonSelector)) {
    if (!button) return null;
    const computed = window.getComputedStyle(order);
    const background = computed.getPropertyValue('--lp-cod-button-background').trim() || computed.getPropertyValue('--lp-cta').trim() || '#F65A72';
    const foreground = computed.getPropertyValue('--lp-cod-button-color').trim() || computed.getPropertyValue('--lp-cta-text').trim() || '#192449';
    const shadow = computed.getPropertyValue('--lp-cod-button-shadow').trim() || '0 5px 0 color-mix(in srgb, ' + background + ' 78%, #000)';
    const styles = {
      background,
      color: foreground,
      'border-color': 'transparent',
      'border-radius': '12px',
      'font-size': '16px',
      'box-shadow': shadow
    };
    const signature = JSON.stringify(styles);
    const previous = codButtonStyles.get(button);
    // Compare serialized CSS values after writing them. Browsers normalize hex
    // colors to rgb(), so comparing raw desired strings would loop the observer.
    if (previous && previous.signature === signature && Object.keys(styles).every((property) =>
      button.style.getPropertyValue(property) === previous.values[property]
      && button.style.getPropertyPriority(property) === 'important')) return button;
    Object.entries(styles).forEach(([property, value]) => button.style.setProperty(property, value, 'important'));
    codButtonStyles.set(button, {
      signature,
      values: Object.fromEntries(Object.keys(styles).map((property) => [property, button.style.getPropertyValue(property)]))
    });
    return button;
  }

  function initCodBridge(section, record) {
    if (section.dataset.lpAnchor !== 'order') return;
    styleCodButton(section);
    const observer = new MutationObserver(() => styleCodButton(section));
    observer.observe(section, { subtree: true, childList: true, attributes: true, attributeFilter: ['style', 'class', 'id'] });
    record.cleanups.push(() => observer.disconnect());
    // Releasit mounts its dialog outside the section and uses inline !important
    // colors. Reuse the idempotent style writer, without touching form behavior.
    if (section.dataset.productId === '16345320226897') {
      record.cleanups.push(cartWriteGate.acquire());
      const styleDialog = () => {
        if (!section.isConnected) return;
        const submit = document.querySelector('#_rsi-cod-form-modal-form ._rsi-modal-submit-button');
        if (submit) styleCodButton(section, submit);
      };
      const dialogObserver = new MutationObserver(styleDialog);
      dialogObserver.observe(document.body, { subtree: true, childList: true, attributes: true, attributeFilter: ['style', 'class', 'id'] });
      record.cleanups.push(() => dialogObserver.disconnect());
      styleDialog();
    }
  }

  function setCodBusy(group, busy) {
    if (!group) return;
    elements(group, '[data-lp-cod-trigger]').forEach((trigger) => {
      if (busy) trigger.setAttribute('aria-busy', 'true');
      else trigger.removeAttribute('aria-busy');
    });
  }

  function cancelCodOpen(key) {
    const job = codOpenJobs.get(key);
    if (!job) return;
    window.clearTimeout(job.timer);
    codOpenJobs.delete(key);
    setCodBusy(groupFor(job.order), false);
  }

  function codFallback(group, order, message) {
    if (!order || !order.isConnected) return;
    let status = order.querySelector('[data-lp-cod-status]');
    if (!status) {
      status = document.createElement('p');
      status.className = 'lp-note lp-cod-status';
      status.setAttribute('data-lp-cod-status', '');
      status.setAttribute('role', 'status');
      status.setAttribute('tabindex', '-1');
      (order.querySelector('.lp-order-card') || order).append(status);
    }
    status.textContent = message;
    status.hidden = false;
    moveToSection(group, 'order');
    status.focus({ preventScroll: true });
  }

  function readyCodButton(order) {
    const button = styleCodButton(order);
    return button && button.isConnected && shown(button) && !button.disabled
      && button.getAttribute('aria-disabled') !== 'true'
      && !button.closest('[inert], [hidden], [aria-hidden="true"]') ? button : null;
  }

  function requestCodOpen(group, trigger) {
    const order = group.sections.find((section) => section.dataset.lpAnchor === 'order');
    if (!order || order.dataset.purchaseMode !== 'cod') return false;
    const key = keyFor(order);
    if (codOpenJobs.has(key)) return true;
    const status = availability(group, group.quantity);
    if (!status.valid) { codFallback(group, order, status.message); return true; }
    if (group.busy) { codFallback(group, order, 'Espera a que termine la operación en curso antes de continuar.'); return true; }
    const previousStatus = order.querySelector('[data-lp-cod-status]');
    if (previousStatus) previousStatus.hidden = true;
    const job = { order, trigger, deadline: Date.now() + 15000, timer: 0, syncedButton: null, syncedPack: '', done: false, waitedForCart: false };
    codOpenJobs.set(key, job);
    setCodBusy(group, true);
    const attempt = () => {
      if (codOpenJobs.get(key) !== job || job.done) return;
      const current = groupFor(order);
      if (!current || !order.isConnected) { cancelCodOpen(key); return; }
      const currentStatus = availability(current, current.quantity);
      if (!currentStatus.valid) {
        cancelCodOpen(key);
        codFallback(current, order, currentStatus.message);
        return;
      }
      if (Date.now() >= job.deadline) {
        cancelCodOpen(key);
        codFallback(current, order, job.waitedForCart
          ? 'El formulario sigue terminando la operación anterior. Espera unos segundos y vuelve a pulsar Pedir ahora.'
          : 'El formulario todavía no está listo. Puedes abrirlo desde el botón de compra de esta sección cuando aparezca.');
        return;
      }
      if (!cartWriteGate.ready()) {
        job.waitedForCart = true;
        job.timer = window.setTimeout(attempt, Math.min(100, job.deadline - Date.now()));
        return;
      }
      let button = readyCodButton(order);
      if (button) {
        if (job.syncedButton !== button || job.syncedPack !== selectedPack(current)) {
          sync(current);
          const variantInput = order.querySelector('[data-lp-variant-input]');
          const quantityInput = order.querySelector('[data-lp-quantity-input]');
          if (!variantInput || !quantityInput || variantInput.disabled || quantityInput.disabled
            || variantInput.value !== current.variantId || quantityInput.value !== String(current.quantity)) {
            cancelCodOpen(key);
            codFallback(current, order, 'No pudimos preparar la selección. Revisa tu opción en esta sección antes de continuar.');
            return;
          }
          job.syncedButton = button;
          job.syncedPack = selectedPack(current);
          [variantInput, quantityInput].forEach((input) => input.dispatchEvent(new Event('change', { bubbles: true })));
          // Give the app's real change handlers a turn to consume the selection.
          job.timer = window.setTimeout(attempt, 0);
          return;
        }
        button = readyCodButton(order);
        if (button) {
          job.done = true;
          setCodBusy(current, false);
          // A short cooldown also absorbs a rapid second click while the app
          // creates its modal. It does not submit or confirm any order.
          job.timer = window.setTimeout(() => cancelCodOpen(key), 500);
          try {
            forwardingCodClick = true;
            button.click();
          } catch (_) {
            cancelCodOpen(key);
            codFallback(current, order, 'No pudimos abrir el formulario. Usa el botón de compra de esta sección para volver a intentarlo.');
          } finally { forwardingCodClick = false; }
          return;
        }
      }
      job.timer = window.setTimeout(attempt, Math.min(100, job.deadline - Date.now()));
    };
    job.timer = window.setTimeout(attempt, 0);
    return true;
  }

  function rebuild() {
    const previous = groups;
    groups = new Map();
    sections.forEach((record, section) => {
      if (!section.isConnected) return;
      const key = keyFor(section);
      if (!groups.has(key)) groups.set(key, { sections: [], data: null, busy: false });
      groups.get(key).sections.push(section);
    });
    groups.forEach((group, key) => {
      const source = group.sections.find((section) => section.dataset.lpAnchor === 'order' && sections.get(section).data)
        || group.sections.find((section) => sections.get(section).data);
      if (source) group.data = sections.get(source).data;
      group.packsReady = !source || source.dataset.lpPacksReady !== 'false';
      group.nativeBlocked = Boolean(source && source.dataset.lpCodQuantityOffers === 'true');
      group.codQuantityOffers = Boolean(group.nativeBlocked && source.dataset.purchaseMode === 'cod' && group.data && group.data.currency === 'CLP');
      const old = previous.get(key);
      if (group.data && group.data.variants.length) {
        const oldVariant = old && group.data.variants.find((variant) => variant.id === old.variantId);
        const oldCard = oldVariant && choiceForVariant(group, old.variantId, old.quantity);
        const configuredSelection = !source || source.dataset.lpRequireConfiguredPacks !== 'true' || oldCard;
        const canKeep = oldVariant && configuredSelection && availability(Object.assign({}, group, { variantId: old.variantId, units: oldCard ? oldCard.units : old.units, packsReady: true }), old.quantity).valid;
        if (canKeep) {
          const card = choiceForVariant(group, old.variantId, old.quantity);
          group.variantId = old.variantId;
          group.quantity = old.quantity;
          group.units = card ? card.units : old.units || 1;
        } else {
          const choices = packChoices(group);
          const card = choices.find((choice) => choice.variantId === group.data.selectedVariantId && choice.available)
            || choices.find((choice) => choice.available);
          const chosen = group.data.variants.find((variant) => variant.id === (card ? card.variantId : group.data.selectedVariantId))
            || group.data.variants.find((variant) => variant.available)
            || group.data.variants[0];
          const selectedCard = card || choiceForVariant(group, chosen.id);
          group.variantId = chosen.id;
          group.quantity = selectedCard ? selectedCard.quantity : Math.max(1, chosen.minimum || 1);
          group.units = selectedCard ? selectedCard.units : 1;
        }
        group.busy = Boolean(old && old.busy);
        sync(group);
      }
      resolveAnchors(group);
    });
  }

  function initGallery(section, record) {
    const gallery = section.querySelector('[data-lp-gallery]');
    if (!gallery) return;
    const slides = Array.from(gallery.querySelectorAll('[data-lp-slide]'));
    const dialog = gallery.querySelector('[data-lp-lightbox]');
    let index = 0;
    let lastTrigger;
    const on = (element, name, handler) => {
      element.addEventListener(name, handler);
      record.cleanups.push(() => element.removeEventListener(name, handler));
    };
    const show = (next) => {
      if (!slides.length) return;
      const slideFocused = slides.includes(document.activeElement);
      index = (next + slides.length) % slides.length;
      slides.forEach((slide, position) => { slide.hidden = position !== index; });
      if (slideFocused) slides[index].focus();
      const count = gallery.querySelector('[data-lp-gallery-count]');
      if (count) count.textContent = (index + 1) + ' / ' + slides.length;
    };
    on(gallery, 'click', (event) => {
      const control = event.target.closest('button, [data-lp-zoom]');
      if (!control) return;
      if (control.matches('[data-lp-gallery-prev]')) show(index - 1);
      if (control.matches('[data-lp-gallery-next]')) show(index + 1);
      if (control.matches('[data-lp-lightbox-close]') && dialog) dialog.close();
      if (control.matches('[data-lp-zoom]') && dialog && typeof dialog.showModal === 'function') {
        event.preventDefault();
        const image = dialog.querySelector('[data-lp-lightbox-image]');
        image.src = control.href;
        image.alt = control.querySelector('img')?.alt || '';
        lastTrigger = control;
        dialog.showModal();
        dialog.querySelector('[data-lp-lightbox-close]').focus();
        scheduleSticky();
      }
    });
    on(gallery, 'keydown', (event) => {
      if (dialog && dialog.open) return;
      if (event.key === 'ArrowLeft' || event.key === 'ArrowRight') {
        event.preventDefault();
        show(index + (event.key === 'ArrowRight' ? 1 : -1));
      }
    });
    if (dialog) {
      on(dialog, 'click', (event) => { if (event.target === dialog) dialog.close(); });
      on(dialog, 'close', () => { if (lastTrigger && lastTrigger.isConnected) lastTrigger.focus(); scheduleSticky(); });
      record.cleanups.push(() => { if (dialog.open) dialog.close(); });
    }
  }

  const offerObserver = 'IntersectionObserver' in window ? new IntersectionObserver((entries) => {
    entries.forEach((entry) => {
      if (!entry.isIntersecting) return;
      const record = sections.get(entry.target);
      if (!record || record.offerSeen) return;
      record.offerSeen = true;
      emit('lp:offer_view', entry.target, groupFor(entry.target));
      offerObserver.unobserve(entry.target);
    });
  }, { threshold: 0.15 }) : null;

  function init(scope) {
    const found = Array.from(scope.querySelectorAll(sectionSelector));
    if (scope instanceof Element && scope.matches(sectionSelector)) found.unshift(scope);
    found.forEach((section) => {
      if (sections.has(section)) return;
      const record = { cleanups: [], data: null, offerSeen: false };
      const json = section.querySelector('[data-lp-product-json]');
      if (json) {
        try {
          const parsed = JSON.parse(json.textContent);
          if (parsed && Array.isArray(parsed.variants)) record.data = parsed;
        } catch (_) { /* The visible native variant/quantity fields remain usable. */ }
      }
      sections.set(section, record);
      section.querySelectorAll('[data-lp-enhance]').forEach((element) => {
        if (record.data || element.closest('[data-lp-gallery]')) element.hidden = false;
      });
      initGallery(section, record);
      initCodBridge(section, record);
      if (offerObserver && section.matches('[data-lp-offer]')) offerObserver.observe(section);
    });
    rebuild();
    scheduleSticky();
  }

  function unload(scope) {
    codOpenJobs.forEach((job, key) => {
      if (job.order === scope || scope.contains(job.order) || job.trigger === scope || scope.contains(job.trigger)) cancelCodOpen(key);
    });
    sections.forEach((record, section) => {
      if (section !== scope && !scope.contains(section)) return;
      if (offerObserver) offerObserver.unobserve(section);
      record.cleanups.forEach((cleanup) => cleanup());
      sections.delete(section);
    });
    rebuild();
    scheduleSticky();
  }

  function select(group, section, variantId, quantity, units, location) {
    if (!group || !group.data || group.busy || !group.data.variants.some((variant) => variant.id === variantId)) return;
    if (group.codQuantityOffers && offerTotalCents(group, variantId, quantity, units) === null) { sync(group); return; }
    group.variantId = variantId;
    group.quantity = quantity;
    group.units = Math.max(1, units || 1);
    elements(group, '[data-lp-error], [data-lp-success]').forEach((node) => { node.hidden = true; });
    sync(group);
    elements(group, '[data-lp-variant-input], [data-lp-quantity-input]').forEach((input) => {
      input.dispatchEvent(new Event('change', { bubbles: true }));
    });
    // Merchants can consume this documented event to integrate an official COD API.
    // No app selectors or undocumented app methods are called here.
    emit('lp:pack_select', section, group, { location });
  }

  function shown(element) {
    if (!element || element.hidden) return false;
    const style = window.getComputedStyle(element);
    return style.display !== 'none' && style.visibility !== 'hidden' && element.getClientRects().length > 0;
  }

  function updateSticky() {
    stickyFrame = 0;
    const mobile = window.matchMedia('(max-width: 749px)').matches;
    const active = document.activeElement;
    const inputFocused = active && active.matches('input, textarea, select, [contenteditable="true"]');
    const dialogOpen = Array.from(document.querySelectorAll('dialog[open], [role="dialog"][aria-modal="true"], cart-drawer.active, #cart-notification.active')).some(shown);
    groups.forEach((group) => {
      const hero = group.sections.find((section) => section.dataset.lpAnchor === 'hero');
      const order = group.sections.find((section) => section.dataset.lpAnchor === 'order');
      const heroPast = hero && hero.getBoundingClientRect().bottom <= 0;
      const orderRect = order && order.getBoundingClientRect();
      const orderVisible = orderRect && orderRect.top < window.innerHeight && orderRect.bottom > 0;
      const orderFocused = order && active && order.contains(active);
      group.sections.filter((section) => section.hasAttribute('data-lp-mobile-bar')).forEach((bar) => {
        bar.hidden = !(mobile && heroPast && order && !orderVisible && !orderFocused && !inputFocused && !dialogOpen);
      });
    });
  }

  function scheduleSticky() {
    if (!stickyFrame) stickyFrame = window.requestAnimationFrame(updateSticky);
  }

  function showError(form, text) {
    const error = form.querySelector('[data-lp-error]');
    if (!error || !form.isConnected) return;
    error.textContent = text;
    const group = groupFor(sectionFor(form));
    if (group && group.data) {
      const link = document.createElement('a');
      link.href = group.data.cartUrl;
      link.textContent = ' Ver mi carrito';
      error.append(link);
    }
    error.hidden = false;
    error.focus();
  }

  async function addToCart(form, section, group) {
    if (group.nativeBlocked) { showError(form, 'Esta oferta se compra exclusivamente mediante el formulario de contraentrega.'); return; }
    if (group.busy) return;
    const status = availability(group, group.quantity);
    if (!status.valid) { showError(form, status.message); return; }
    const submitted = publicDetail(section, group, { location: 'order' });
    const data = new FormData(form);
    let cart = document.querySelector('cart-notification') || document.querySelector('cart-drawer');
    let cartSections = [];
    try {
      if (cart && typeof cart.getSectionsToRender === 'function' && typeof cart.renderContents === 'function') {
        cartSections = cart.getSectionsToRender().map((item) => String(item.id)).filter(Boolean).slice(0, 5);
        if (cartSections.length) {
          data.set('sections', cartSections.join(','));
          data.set('sections_url', window.location.pathname);
        }
        if (typeof cart.setActiveElement === 'function') cart.setActiveElement(document.activeElement);
      } else cart = null;
    } catch (_) { cart = null; }
    group.busy = true;
    elements(group, '[data-lp-error], [data-lp-success]').forEach((node) => { node.hidden = true; });
    sync(group);
    let result;
    try {
      const response = await fetch(group.data.cartAddUrl, {
        method: 'POST', credentials: 'same-origin',
        headers: { Accept: 'application/json', 'X-Requested-With': 'XMLHttpRequest' },
        body: data
      });
      result = await response.json();
      if (!response.ok || result.status) {
        const reason = typeof result.description === 'string' ? result.description : typeof result.message === 'string' ? result.message : 'No se pudo confirmar esta selección.';
        showError(form, reason + ' Revisa tu carrito antes de volver a intentar.');
        return;
      }
      if (!result || !result.id) throw new Error('Unconfirmed cart response');
    } catch (_) {
      // The server may already have accepted an interrupted request. Never retry automatically.
      showError(form, 'No pudimos confirmar la respuesta. Revisa tu carrito antes de volver a intentar.');
      return;
    } finally {
      group.busy = false;
      const current = groupFor(section);
      if (current) { current.busy = false; sync(current); }
    }
    // The add succeeded. A later cart drawer/rendering failure must not become an add error.
    (section.isConnected ? section : document).dispatchEvent(new CustomEvent('lp:add_to_cart', { bubbles: true, detail: submitted }));
    const success = form.querySelector('[data-lp-success]');
    if (success && form.isConnected) {
      success.querySelector('[data-lp-success-message]').textContent = 'Producto agregado al carrito. Revisa el pedido para continuar.';
      success.hidden = false;
      success.focus();
    }
    if (cart && cartSections.length && result.sections && cartSections.every((id) => typeof result.sections[id] === 'string')) {
      try {
        cart.classList.remove('is-empty');
        cart.renderContents(result);
      } catch (_) {
        if (success) success.querySelector('[data-lp-success-message]').textContent = 'Producto agregado. Abre tu carrito para revisar el pedido.';
      }
    }
    scheduleSticky();
  }

  function checkoutLink(anchor) {
    try {
      const url = new URL(anchor.href, window.location.href);
      return url.origin === window.location.origin && /\/checkouts?(?:\/|$)/.test(url.pathname);
    } catch (_) { return false; }
  }

  // Route the app's visible opener through the same readiness gate as the hero
  // and floating links. Forwarded clicks still reach its original handlers.
  document.addEventListener('click', (event) => {
    if (forwardingCodClick || !(event.target instanceof Element)) return;
    const button = event.target.closest(codButtonSelector);
    if (!button) return;
    const section = sectionFor(button);
    if (!section || section.dataset.productId !== '16345320226897') return;
    const group = groupFor(section);
    if (!group || !requestCodOpen(group, button)) return;
    event.preventDefault();
    event.stopImmediatePropagation();
  }, true);

  document.addEventListener('click', (event) => {
    if (forwardingCodClick || !(event.target instanceof Element)) return;
    const section = sectionFor(event.target);
    const group = groupFor(section);
    if (!section || !group) return;
    const card = event.target.closest('[data-lp-pack]');
    if (card && !card.disabled) {
      select(group, section, card.dataset.variantId, Number(card.dataset.quantity), Number(card.dataset.unitsPerItem), section.dataset.sectionName);
      if (card.dataset.lpPackTarget) {
        emit('lp:cta_click', section, group, { location: 'packs-card' });
        if (card.hasAttribute('data-lp-cod-trigger') && requestCodOpen(group, card)) return;
        moveToSection(group, card.dataset.lpPackTarget);
      }
      return;
    }
    const anchor = event.target.closest('a');
    if (!anchor) return;
    if (anchor.hasAttribute('data-lp-target')) resolveAnchors(group);
    if (anchor.dataset.ctaLocation || anchor.hasAttribute('data-lp-target')) emit('lp:cta_click', section, group, { location: anchor.dataset.ctaLocation || section.dataset.sectionName });
    if (anchor.hasAttribute('data-lp-cod-trigger') && event.button === 0 && !event.metaKey && !event.ctrlKey && !event.shiftKey && !event.altKey) {
      if (requestCodOpen(group, anchor)) { event.preventDefault(); return; }
    }
    if (checkoutLink(anchor) && !event.defaultPrevented && event.button === 0) emit('lp:begin_checkout', section, group, { location: 'checkout-link' });
    if (!anchor.hasAttribute('data-lp-target') || event.button !== 0 || event.metaKey || event.ctrlKey || event.shiftKey || event.altKey) return;
    const target = group.sections.find((candidate) => candidate.dataset.lpAnchor === anchor.dataset.lpTarget);
    if (!target) return;
    event.preventDefault();
    moveToSection(group, anchor.dataset.lpTarget);
  });

  document.addEventListener('change', (event) => {
    const input = event.target;
    if (!(input instanceof Element)) return;
    const section = sectionFor(input);
    const group = groupFor(section);
    if (!group || !group.data) return;
    if (input.matches('[data-lp-variant-select]')) {
      const variant = group.data.variants.find((candidate) => candidate.id === input.value);
      if (variant) {
        const card = choiceForVariant(group, variant.id);
        select(group, section, variant.id, card ? card.quantity : Math.max(1, variant.minimum || 1), card ? card.units : 1, 'variant-select');
      }
    }
    if (input.matches('[data-lp-quantity-control]')) select(group, section, group.variantId, Number(input.value), group.units, 'quantity');
  });

  document.addEventListener('submit', (event) => {
    const form = event.target;
    if (!(form instanceof HTMLFormElement)) return;
    const section = sectionFor(form);
    const group = groupFor(section);
    if (!group || !group.data || !form.matches('[data-lp-native-form]')) return;
    if (event.defaultPrevented) return;
    if (group.nativeBlocked) { event.preventDefault(); showError(form, 'Esta oferta se compra exclusivamente mediante el formulario de contraentrega.'); return; }
    if (group.busy) { event.preventDefault(); return; }
    const status = availability(group, group.quantity);
    if (!status.valid) { event.preventDefault(); showError(form, status.message); return; }
    emit('lp:cta_click', section, group, { location: 'native-cart' });
    if (form.dataset.lpAjax !== 'true' || !window.fetch) {
      group.busy = true;
      sync(group);
      return;
    }
    event.preventDefault();
    addToCart(form, section, group);
  });

  document.addEventListener('toggle', (event) => {
    const details = event.target;
    if (!(details instanceof HTMLDetailsElement) || !details.open) return;
    const section = sectionFor(details);
    if (section && section.dataset.lpAnchor === 'faq') emit('lp:faq_open', section, groupFor(section), { item_id: details.id || '' });
    scheduleSticky();
  }, true);
  document.addEventListener('play', (event) => {
    const section = sectionFor(event.target);
    if (section && event.target instanceof HTMLVideoElement) emit('lp:video_play', section, groupFor(section), { media_id: event.target.id || '' });
  }, true);
  document.addEventListener('focusin', scheduleSticky);
  document.addEventListener('focusout', scheduleSticky);
  document.addEventListener('close', scheduleSticky, true);
  window.addEventListener('scroll', scheduleSticky, { passive: true });
  window.addEventListener('resize', scheduleSticky, { passive: true });
  if (window.visualViewport) window.visualViewport.addEventListener('resize', scheduleSticky, { passive: true });
  document.addEventListener('shopify:section:load', (event) => init(event.target));
  document.addEventListener('shopify:section:unload', (event) => unload(event.target));
  const modalObserver = new MutationObserver((records) => {
    if (records.some((record) => record.type === 'childList' || record.target.matches && record.target.matches('dialog, [role="dialog"], cart-drawer, #cart-notification'))) scheduleSticky();
  });
  modalObserver.observe(document.documentElement, { subtree: true, childList: true, attributes: true, attributeFilter: ['open', 'aria-hidden', 'class', 'style'] });
  window.LPCommerce = { init, version: '1.0.0' };
  if (document.readyState === 'loading') document.addEventListener('DOMContentLoaded', () => init(document), { once: true });
  else init(document);
}());
